<template>
  <ResizablePanels :show-right="showingPreview">
    <template #panel-1>
      <Detail.Container
        :breadcrumbs="props.breadcrumbs"
        :has-unsaved-changes="hasUnsavedChanges"
        :saving="savingChanges"
        :save="saveChanges"
        :reset-changes="resetChanges"
      >
        <template #actions>
          <EditorModeDropdown
            :id="props.transactionalEmail || 'unknown'"
            v-model="mode"
            v-model:template="template"
            :newsletter="props.newsletter"
            :subject="title ?? 'Hello, world'"
          />
          <List.Action
            text="Preview"
            hotkey="$mod+p"
            :route="null"
            variant="secondary"
            @click="showingPreview = !showingPreview"
          />
        </template>

        <template #callouts>
          <FancyParseWarningCallout
            v-if="showFancyParseWarning"
            @close="showFancyParseWarning = false"
            @switch="mode = 'plaintext'"
          />
          <div
            v-if="props.warning"
            class="@container @lg:items-center @lg:flex-row @lg:py-2 flex flex-col gap-2 border-y border-y-yellow-300 bg-yellow-100 px-4 py-3 text-yellow-600 first:border-t-0"
          >
            <span class="grow text-sm">{{ props.warning }}</span>
          </div>
        </template>

        <template #body>
          <!--
            We want the editor to never get obstructed by the action widget.

            `mb-16` is derived from 80px - 16px (`-m-4` on this scroller)
            `mb-4 pb-12` is derived from 80px - 48px (`-bottom-12` on the action widget)
          -->
          <div
            ref="scrollRef"
            class="peer relative -m-4 flex h-full min-h-0 flex-1 basis-0 flex-col overflow-y-auto pt-4"
            :class="[!isMobile ? 'mb-4 pb-12' : 'mb-16']"
          >
            <!-- Suspense is keyed so we'll show the fallback on mode changes -->
            <Suspense :key="mode">
              <TiptapEditor
                v-if="mode === 'fancy'"
                v-model:title="title"
                v-model:body="body"
                :extensions="extensions"
                :template-tags="templateTags"
                @parse-error="showFancyParseWarning = true"
              />
              <PlainEditor
                v-else-if="mode === 'plaintext'"
                v-model:title="title"
                v-model:body="body"
              />
              <NakedEditor v-else-if="mode === 'naked'" v-model:body="body" />

              <template #fallback>
                <EditorSkeleton />
              </template>
            </Suspense>
          </div>
        </template>
      </Detail.Container>
    </template>

    <template #panel-2>
      <PreviewPanel
        :status="generatingPreview ? 'pending' : 'success'"
        :body="compiledBody"
        :class="{
          // Padding to avoid <ChangeWidget> obstructing page content
          'pb-action-gutter': hasUnsavedChanges,
        }"
        :contexts="props.previewContexts || ['email', 'web']"
        :draft-to="props.draftTo"
        @update:showing-preview="showingPreview = $event"
      />
    </template>
  </ResizablePanels>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, ref, watch } from "vue";

import ResizablePanels, {
  useShowingPreview,
} from "@/components/Layout/ResizablePanels.vue";
import { Props as ViewProps } from "@/design_system/View";
import Detail from "@/design_system/View/Detail";
import List from "@/design_system/View/List";
import { writable } from "@/lib/dirty";
import { useMediaQuery } from "@/lib/media_query";
import { sampleEmail } from "@/lib/utils";
import { RouteLocation } from "@/router/types";
import EditorSkeleton from "@/screens/EmailDetail/editor/EditorSkeleton.vue";
import FancyParseWarningCallout from "@/screens/EmailDetail/editor/FancyParseWarningCallout.vue";
import EditorModeDropdown from "@/screens/EmailDetail/EditorModeDropdown.vue";
import PreviewPanel from "@/screens/EmailDetail/PreviewPanel.vue";
import {
  EditorMode,
  LocalStorageKey,
  safeLocalStorageGetItem,
} from "@/store/local_storage";
import { useStore as useEmailsStore } from "@/store/stores/emails";
import { NewsletterEmailTemplate } from "@/types/email";
import { Newsletter } from "@/types/newsletter";
import { Context } from "@/types/preview";
import {
  asynchronouslyRenderMarkdown,
  asynchronouslyRenderSubPage,
  debounce,
  RenderedMarkdown,
  TransactionalEmail,
} from "@/utils";

const PlainEditor = defineAsyncComponent({
  loader: () => import("@/screens/EmailDetail/editor/PlainEditor.vue"),
});
const TiptapEditor = defineAsyncComponent({
  loader: () => import("@/screens/EmailDetail/editor/TiptapEditor.vue"),
});
const NakedEditor = defineAsyncComponent({
  loader: () => import("@/screens/EmailDetail/editor/NakedEditor.vue"),
});

const emailsStore = useEmailsStore();

const isMobile = useMediaQuery("(width < 768px)");

// "Scaffolding" refers to things like the "footer" and "header" that are
// not actually emails, but are used to render the email preview. For these, we don't want
// to use the model (e.g. the footer being edited) as the body of the preview, since the preview
// is smart enough to pull that in anyway which means we'd be showing the given model _twice_.
type PreviewTemplate = "email" | "subscribe" | "scaffolding";

const props = defineProps<
  {
    previewContexts?: Context[];
    previewTemplate?: PreviewTemplate;
    transactionalEmail?: TransactionalEmail;
    save: () => Promise<void>;
    newsletter: Newsletter;
    draftTo?: RouteLocation;
    extensions?: string[];
    templateTags?: string[];
    externalFeedId?: string;
    automationId?: string;
    warning?: string;
    playground?: boolean;
  } & ViewProps
>();

const body = defineModel<string>({ required: true });
const title = defineModel<string>("title");
const template = defineModel<NewsletterEmailTemplate | undefined>("template");

const initialBody = ref(body.value);
const initialTitle = ref(title.value);

const mode = ref<EditorMode>(
  (new URLSearchParams(window.location.search).get(
    "editor_mode"
  ) as EditorMode) || "fancy"
);
const showingPreview = useShowingPreview();

const generatingPreview = ref(false);
const compiledBody = ref<RenderedMarkdown>({
  web: "",
  email: "",
  email_free: "",
  web_free: "",
});

const showFancyParseWarning = ref(false);

const savingChanges = ref(false);

const saveChanges = async () => {
  try {
    savingChanges.value = true;
    await props.save();
  } finally {
    savingChanges.value = false;
  }

  initialTitle.value = title.value;
  initialBody.value = body.value;
};

const hasUnsavedChanges = computed(() => {
  if (props.playground) {
    return false;
  }
  return title.value !== initialTitle.value || body.value !== initialBody.value;
});

const resetChanges = () => {
  body.value = initialBody.value;
  title.value = initialTitle.value;
};

{
  const urlMode = new URLSearchParams(window.location.search).get(
    "editor_mode"
  ) as EditorMode;

  if (urlMode) {
    mode.value = urlMode;
  } else {
    const savedMode = safeLocalStorageGetItem(LocalStorageKey.EDITOR_MODE);

    if (template.value) {
      mode.value = "naked";
    } else if (savedMode === "plaintext") {
      mode.value = savedMode;
    } else {
      mode.value = "fancy";
    }
  }

  watch(mode, (next) => {
    localStorage.setItem(LocalStorageKey.EDITOR_MODE, next);
  });
}

{
  const isPreviewStale = writable(() => {
    // Invalidate this state if latest email changes
    emailsStore.latestEmail?.id;
    return true;
  });

  watch(
    () => showingPreview.value && isPreviewStale.value,
    async (shouldRegeneratePreview) => {
      if (!shouldRegeneratePreview) {
        return;
      }

      generatingPreview.value = true;
      isPreviewStale.value = false;

      try {
        if (props.previewTemplate == "subscribe") {
          compiledBody.value = await asynchronouslyRenderSubPage(
            props.newsletter
          );
        } else {
          // If the model being previewed can _only_ be previewed as an email, we assume
          // that the model is the email body. Otherwise, grab a sample email.
          const content =
            props.previewContexts?.includes("email") &&
            props.previewContexts.length == 1 &&
            props.previewTemplate != "scaffolding"
              ? body.value
              : emailsStore.latestEmail?.id || sampleEmail.body;

          compiledBody.value = await asynchronouslyRenderMarkdown(
            title.value ??
              (emailsStore.latestEmail?.subject || sampleEmail.subject),
            content,
            undefined,
            props.newsletter,
            mode.value === "naked" ? "naked" : undefined,
            props.transactionalEmail,
            props.externalFeedId,
            props.automationId
          );
        }
      } finally {
        generatingPreview.value = false;
      }
    },
    { immediate: true }
  );

  watch(
    body,
    debounce(() => {
      isPreviewStale.value = true;
    }, 500)
  );

  watch(
    title,
    debounce(() => {
      isPreviewStale.value = true;
    }, 500)
  );
}
</script>
